export const TEAM_COLORS = ["#6C5CE7", "#E8A93D", "#4FA8E0", "#E0729A"];
export const DEFAULT_TEAM_NAMES = [
  "Equipe Violeta",
  "Equipe Dourada",
  "Equipe Azul",
  "Equipe Rosa",
];
export const MIN_TEAMS = 2;
export const MAX_TEAMS = 4;

export const TIME_LIMITS = { min: 5, max: 120, step: 5 };
export const QUESTION_LIMITS = { min: 3, max: 20 };
export const FONT_LIMITS = { min: 14, max: 56, step: 2 };

export interface GameOption {
  res: string;
  correct: boolean;
}

export interface GameQuestion {
  id: number;
  questao: string;
  options: GameOption[];
}

export type Level = "easy" | "medium" | "hard";

export interface TemaOption {
  id: number;
  nome: string;
  total: number;
  /** perguntas validadas por nível */
  levels: Record<Level, number>;
}

export interface TeamResult {
  index: number;
  name: string;
  color: string;
  correct: number;
  wrong: number;
  score: number;
}

export const clamp = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

export function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

const LEVELS: Level[] = ["easy", "medium", "hard"];

/** Divisão das perguntas de cada equipe: 20% fáceis, 40% médias, 40% difíceis. */
export const LEVEL_SHARE: Record<Level, number> = {
  easy: 0.2,
  medium: 0.4,
  hard: 0.4,
};

/** Quando falta um nível, o excedente vai para o nível mais próximo. */
const FALLBACK_ORDER: Record<Level, Level[]> = {
  easy: ["medium", "hard"],
  medium: ["hard", "easy"],
  hard: ["medium", "easy"],
};

/** Reparte `n` perguntas pelos níveis (maior resto; em empate prefere média e difícil). */
export function levelQuotas(n: number): Record<Level, number> {
  const quotas = { easy: 0, medium: 0, hard: 0 };
  const rest: { level: Level; frac: number }[] = [];
  let assigned = 0;
  for (const level of ["medium", "hard", "easy"] as Level[]) {
    const exact = n * LEVEL_SHARE[level];
    quotas[level] = Math.floor(exact + 1e-9);
    assigned += quotas[level];
    rest.push({ level, frac: exact - quotas[level] });
  }
  rest.sort((a, b) => b.frac - a.frac); // sort estável: mantém medium > hard > easy
  for (let i = 0; assigned < n; i = (i + 1) % rest.length, assigned++) {
    quotas[rest[i].level] += 1;
  }
  return quotas;
}

/** Máximo de perguntas por equipe que o tema suporta respeitando "todas as equipes iguais". */
export function maxPerTeam(levels: Record<Level, number>, teams: number) {
  return LEVELS.reduce((sum, l) => sum + Math.floor(levels[l] / teams), 0);
}

/**
 * Quota por nível para cada equipe, ajustada ao que existe no tema
 * (cada equipe recebe perguntas distintas, logo a capacidade é `disponíveis / equipes`).
 */
export function planQuotas(
  perTeam: number,
  teams: number,
  available: Record<Level, number>,
): Record<Level, number> {
  const cap = (l: Level) => Math.floor(available[l] / teams);
  const quotas = levelQuotas(perTeam);
  for (const level of LEVELS) {
    let excess = quotas[level] - cap(level);
    if (excess <= 0) continue;
    quotas[level] -= excess;
    for (const other of FALLBACK_ORDER[level]) {
      const take = Math.min(Math.max(0, cap(other) - quotas[other]), excess);
      quotas[other] += take;
      excess -= take;
    }
  }
  return quotas;
}

const normalize = (text: string) => text.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * Monta a partida: `perTeam` perguntas por equipe, 20/40/40 por nível, sem repetições.
 * As rondas seguem a ordem fácil → média → difícil; em cada uma todas as equipes recebem o
 * mesmo nível (perguntas diferentes), e a pergunta `i` é da equipe `i % teams`. `avoidIds` (partida anterior) só entra em último caso.
 */
export function buildQuestions(
  quiz: { easy?: any[]; medium?: any[]; dificult?: any[] },
  perTeam: number,
  teams: number,
  avoidIds: number[] = [],
): GameQuestion[] {
  const avoid = new Set(avoidIds);
  const seenIds = new Set<number>();
  const seenText = new Set<string>();

  const prepare = (raw: any[] = []) =>
    raw.filter((q) => {
      const valid =
        typeof q?.questao === "string" &&
        Array.isArray(q.respostas) &&
        q.respostas.length >= 2 &&
        q.respostas.some((r: any) => r.status) &&
        q.respostas.every((r: any) => r.res);
      if (!valid) return false;
      // nunca a mesma pergunta duas vezes (mesmo id ou mesmo texto)
      const text = normalize(q.questao);
      if (seenIds.has(q.id) || seenText.has(text)) return false;
      seenIds.add(q.id);
      seenText.add(text);
      return true;
    });

  const pools: Record<Level, any[]> = {
    easy: prepare(quiz.easy),
    medium: prepare(quiz.medium),
    hard: prepare(quiz.dificult),
  };

  const available = {
    easy: pools.easy.length,
    medium: pools.medium.length,
    hard: pools.hard.length,
  };
  const quotas = planQuotas(perTeam, teams, available);

  // sorteia, preferindo perguntas que não saíram na partida anterior
  const picked: Record<Level, GameQuestion[]> = { easy: [], medium: [], hard: [] };
  for (const level of LEVELS) {
    const fresh = shuffle(pools[level].filter((q) => !avoid.has(q.id)));
    const seen = shuffle(pools[level].filter((q) => avoid.has(q.id)));
    picked[level] = [...fresh, ...seen].slice(0, quotas[level] * teams).map((q) => ({
      id: q.id,
      questao: q.questao,
      options: shuffle(
        q.respostas.map((r: any) => ({ res: r.res, correct: !!r.status })),
      ) as GameOption[],
    }));
  }

  // ordem das rondas: sempre fáceis → médias → difíceis;
  // todas as equipes enfrentam o mesmo nível na ronda
  const rounds = LEVELS.flatMap((level) => Array<Level>(quotas[level]).fill(level));

  const questions: GameQuestion[] = [];
  for (const level of rounds) {
    for (let t = 0; t < teams; t++) {
      const q = picked[level].pop();
      if (!q) return questions.slice(0, questions.length - (questions.length % teams));
      questions.push(q);
    }
  }
  return questions;
}

/**
 * Próxima partida de uma sessão: prefere perguntas que ainda não saíram em nenhuma
 * partida anterior. Só quando as inéditas acabam é que as usadas voltam a entrar,
 * e nesse caso um novo ciclo começa (o histórico recomeça nesta partida).
 */
export function nextGame(
  quiz: { easy?: any[]; medium?: any[]; dificult?: any[] },
  perTeam: number,
  teams: number,
  usedIds: number[],
): { questions: GameQuestion[]; usedIds: number[] } {
  const questions = buildQuestions(quiz, perTeam, teams, usedIds);
  const used = new Set(usedIds);
  const repeated = questions.some((q) => used.has(q.id));
  const ids = questions.map((q) => q.id);
  return { questions, usedIds: repeated ? ids : [...usedIds, ...ids] };
}

/**
 * `results[i]` é o resultado da pergunta i; a equipe da vez é `i % teams`.
 * (true = acerto, false = erro/tempo esgotado)
 */
export function teamOfQuestion(index: number, teamCount: number) {
  return index % teamCount;
}

export function computeTeams(
  names: string[],
  results: boolean[],
): TeamResult[] {
  const teams: TeamResult[] = names.map((name, index) => ({
    index,
    name,
    color: TEAM_COLORS[index % TEAM_COLORS.length],
    correct: 0,
    wrong: 0,
    score: 0,
  }));

  results.forEach((ok, i) => {
    const team = teams[teamOfQuestion(i, names.length)];
    if (ok) {
      team.correct += 1;
      team.score += 1;
    } else {
      team.wrong += 1;
    }
  });

  return teams;
}

/**
 * Ranking por pontuação. Empatados partilham a posição e a seguinte salta
 * (ex.: dois em 1º → próximo é 3º).
 */
export function rankTeams(teams: TeamResult[]) {
  const sorted = [...teams].sort(
    (a, b) => b.score - a.score || a.index - b.index,
  );
  return sorted.map((team) => ({
    ...team,
    position: sorted.filter((t) => t.score > team.score).length + 1,
  }));
}
